/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { normalizeString, DURATION_HASH_SEED } from './internal/core';
import { getTime, getTimePart, TimePart } from './internal/time';
import { getInstantPart, InstantPart } from './internal/instant';
import { NANOS_OF_DAY, NANOS_OF_EPOCH } from './internal/types';
import { D6, D3 } from "./internal/formats";
import { Components, DateDelta, TimeDelta } from './types';
import { Equatable, Equaler } from '@esfx/equatable';

const durationRegExp = (() => {
    const SIGN = R`[+-]`;
    const DECIMAL = R`${SIGN}?\d+(?:\.\d+)?`;
    const YEARS = R`(?<years>${DECIMAL})Y`;
    const MONTHS = R`(?<months>${DECIMAL})M`;
    const DAYS = R`(?<days>${DECIMAL})D`;
    const HOURS = R`(?<hours>${DECIMAL})H`;
    const MINUTES = R`(?<minutes>${DECIMAL})M`;
    const SECONDS = R`(?<seconds>${DECIMAL})S`;
    return new RegExp(R`^(?<sign>${SIGN})?P(?!$)${YEARS}?${MONTHS}?${DAYS}?(?:T(?!$)${HOURS}?${MINUTES}?${SECONDS}?)?$`);
})();

const PRECISION: (DateDelta | TimeDelta)[] = ["years", "months", "days", "hours", "minutes", "seconds", "milliseconds", "nanoseconds"];

export class Duration implements Equatable {
    static readonly ZERO = new Duration();

    readonly years: number;
    readonly months: number;
    readonly days: number;
    readonly hours: number;
    readonly minutes: number;
    readonly seconds: number;
    readonly milliseconds: number;
    readonly nanoseconds: number;

    constructor(components: Components<DateDelta | TimeDelta> = {}) {
        let { years = 0, months = 0, days = 0, hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = components;
        if (!isFinite(years)) throw new RangeError(`The value ${years} is not valid for 'years' as it is not an finite number.`);
        if (!isFinite(months)) throw new RangeError(`The value ${months} is not valid for 'months' as it is not an finite number.`);
        if (!isFinite(days)) throw new RangeError(`The value ${days} is not valid for 'days' as it is not an finite number.`);
        if (!isFinite(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an finite number.`);
        if (!isFinite(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an finite number.`);
        if (!isFinite(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an finite number.`);
        if (!isFinite(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an finite number.`);
        if (!isFinite(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an finite number.`);

        // normalize accurate duration
        while (nanoseconds <= -1e6) milliseconds--, nanoseconds += 1e6;
        while (nanoseconds >= 1e6) milliseconds++, nanoseconds -= 1e6;
        while (milliseconds <= -1e3) seconds--, milliseconds += 1e3;
        while (milliseconds >= 1e3) seconds++, milliseconds -= 1e3;
        while (seconds <= -60) minutes--, seconds += 60;
        while (seconds >= 60) minutes++, seconds -= 60;
        while (minutes <= -60) hours--, minutes += 60;
        while (minutes >= 60) hours++, minutes -=60;

        minutes += (hours % 1) * 60, hours = Math.trunc(hours);
        seconds += (minutes % 1) * 60, minutes = Math.trunc(minutes);
        milliseconds += (seconds % 1) * 1e3, seconds = Math.trunc(seconds);
        nanoseconds += (milliseconds % 1) * 1e6, milliseconds = Math.trunc(milliseconds);
        nanoseconds = Math.trunc(nanoseconds);

        this.years = years;
        this.months = months;
        this.days = days;
        this.hours = hours;
        this.minutes = minutes;
        this.seconds = seconds;
        this.milliseconds = milliseconds;
        this.nanoseconds = nanoseconds;
    }

    get sign() {
        if (this.years > 0) return 1;
        if (this.months > 0) return 1;
        if (this.days > 0) return 1;
        if (this.hours > 0) return 1;
        if (this.minutes > 0) return 1;
        if (this.seconds > 0) return 1;
        if (this.milliseconds > 0) return 1;
        if (this.nanoseconds > 0) return 1;
        if (this.years < 0) return -1;
        if (this.months < 0) return -1;
        if (this.days < 0) return -1;
        if (this.hours < 0) return -1;
        if (this.minutes < 0) return -1;
        if (this.seconds < 0) return -1;
        if (this.milliseconds < 0) return -1;
        if (this.nanoseconds < 0) return -1;
        return 0;
    }

    plus(deltas: Components<DateDelta | TimeDelta> = {}) {
        const { years = 0, months = 0, days = 0, hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = deltas;
        if (!isFinite(years)) throw new RangeError(`The value ${years} is not valid for 'years' as it is not an finite number.`);
        if (!isFinite(months)) throw new RangeError(`The value ${months} is not valid for 'months' as it is not an finite number.`);
        if (!isFinite(days)) throw new RangeError(`The value ${days} is not valid for 'days' as it is not an finite number.`);
        if (!isFinite(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an finite number.`);
        if (!isFinite(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an finite number.`);
        if (!isFinite(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an finite number.`);
        if (!isFinite(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an finite number.`);
        if (!isFinite(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an finite number.`);
        return new Duration({
            years: this.years + years,
            months: this.months + months,
            days: this.days + days,
            hours: this.hours + hours,
            minutes: this.minutes + minutes,
            seconds: this.seconds + seconds,
            milliseconds: this.milliseconds + milliseconds,
            nanoseconds: this.nanoseconds + nanoseconds,
        });
    }

    minus(deltas: Components<DateDelta | TimeDelta> = {}) {
        const { years = 0, months = 0, days = 0, hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = deltas;
        if (!isFinite(years)) throw new RangeError(`The value ${years} is not valid for 'years' as it is not an finite number.`);
        if (!isFinite(months)) throw new RangeError(`The value ${months} is not valid for 'months' as it is not an finite number.`);
        if (!isFinite(days)) throw new RangeError(`The value ${days} is not valid for 'days' as it is not an finite number.`);
        if (!isFinite(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an finite number.`);
        if (!isFinite(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an finite number.`);
        if (!isFinite(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an finite number.`);
        if (!isFinite(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an finite number.`);
        if (!isFinite(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an finite number.`);
        return new Duration({
            years: this.years - years,
            months: this.months - months,
            days: this.days - days,
            hours: this.hours - hours,
            minutes: this.minutes - minutes,
            seconds: this.seconds - seconds,
            milliseconds: this.milliseconds - milliseconds,
            nanoseconds: this.nanoseconds - nanoseconds,
        });
    }

    scale(amount: number) {
        return new Duration({
            years: this.years * amount,
            months: this.months * amount,
            days: this.days * amount,
            hours: this.hours * amount,
            minutes: this.minutes * amount,
            seconds: this.seconds * amount,
            milliseconds: this.milliseconds * amount,
            nanoseconds: this.nanoseconds * amount,
        });
    }

    with(components: Components<DateDelta | TimeDelta> = {}) {
        const {
            years = this.years,
            months = this.months,
            days = this.days,
            hours = this.hours,
            minutes = this.minutes,
            seconds = this.seconds,
            milliseconds = this.milliseconds,
            nanoseconds = this.nanoseconds,
        } = components;
        return new Duration({
            years,
            months,
            days,
            hours,
            minutes,
            seconds,
            milliseconds,
            nanoseconds,
        });
    }

    negate() {
        return new Duration({
            years: -this.years,
            months: -this.months,
            days: -this.days,
            hours: -this.hours,
            minutes: -this.minutes,
            seconds: -this.seconds,
            milliseconds: -this.milliseconds,
            nanoseconds: -this.nanoseconds,
        });
    }

    abs() {
        return new Duration({
            years: Math.abs(this.years),
            months: Math.abs(this.months),
            days: Math.abs(this.days),
            hours: Math.abs(this.hours),
            minutes: Math.abs(this.minutes),
            seconds: Math.abs(this.seconds),
            milliseconds: Math.abs(this.milliseconds),
            nanoseconds: Math.abs(this.nanoseconds),
        });
    }

    truncate(unit: DateDelta | TimeDelta): Duration {
        const components: Components<DateDelta | TimeDelta> = {};
        for (const key of PRECISION) {
            components[key] = this[key];
            if (key === unit) break;
        }
        return new Duration(components);
    }

    toTimeAgoString() {
        let duration: Duration = this;
        const sign = duration.sign;
        if (sign === 0) return "just now";
        if (sign < 0) duration = duration.negate();

        const parts: string[] = [];
        if (duration.years !== 0) parts.push(formatUnit(duration.years, "years", "year"));
        if (duration.months !== 0) parts.push(formatUnit(duration.months, "months", "month"));
        if (duration.days !== 0) parts.push(formatUnit(duration.days, "days", "day"));
        if (duration.hours !== 0) parts.push(formatUnit(duration.hours, "hours", "hour"));
        if (duration.minutes !== 0) parts.push(formatUnit(duration.minutes, "minutes", "minute"));

        const seconds = duration.seconds + duration.milliseconds / 1e3 + duration.nanoseconds / 1e9;
        if (seconds !== 0) parts.push(formatUnit(seconds, "seconds", "second"));

        let s = "";
        if (sign > 0) s += "in ";
        if (parts.length === 1) {
            s += parts[0];
        }
        else {
            s += parts.slice(0, -1).join(", ");
            s += " and ";
            s += parts[parts.length - 1];
        }
        if (sign < 0) s += " ago";
        return s;
    }

    toString() {
        let duration: Duration = this;
        if (duration.sign === 0) return "P0D";

        let s = "P";
        if (duration.sign < 0) {
            s = "-" + s;
            duration = duration.negate();
        }
        if (duration.years) s += `${duration.years}Y`;
        if (duration.months) s += `${duration.months}M`;
        if (duration.days) s += `${duration.days}D`;
        if (duration.hours || duration.minutes || duration.seconds || duration.milliseconds || duration.nanoseconds) {
            s += `T`;
            if (duration.hours) s += `${duration.hours}H`;
            if (duration.minutes) s += `${duration.minutes}M`;
            let ns = getTime(0, 0, duration.seconds, duration.milliseconds, duration.nanoseconds);
            if (ns) {
                if (ns < 0n) {
                    s += "-";
                    ns = -ns as NANOS_OF_DAY;
                }
                const { second, millisecond, nanosecond } = getTimePart(ns, TimePart.all);
                s += `${second}`;
                if (millisecond || nanosecond) {
                    s += `.${D3(millisecond)}`;
                    if (nanosecond) {
                        s += `${D6(nanosecond)}`;
                    }
                }
                s += `S`;
            }
        }
        return s;
    }

    toJSON() {
        return this.toString();
    }

    equals(other: Duration) {
        return other !== null
            && other !== undefined
            && this.years === other.years
            && this.months === other.months
            && this.days === other.days
            && this.hours === other.hours
            && this.minutes === other.minutes
            && this.seconds === other.seconds
            && this.milliseconds === other.milliseconds
            && this.nanoseconds === other.nanoseconds;
    }

    static fromString(text: string) {
        const match = durationRegExp.exec(normalizeString(text));
        if (match) {
            const { sign, years = "", months = "", days = "", hours = "", minutes = "", seconds = "" } = match.groups!;
            const duration = new Duration({ years: +years, months: +months, days: +days, hours: +hours, minutes: +minutes, seconds: +seconds });
            return sign === "-" ? duration.negate() : duration;
        }

        throw new SyntaxError(`Invalid duration string: '${text}'`);
    }

    static fromMilliseconds(milliseconds: number, nanoseconds: number = 0) {
        return new Duration({ milliseconds, nanoseconds: nanoseconds % 1e6 });
    }

    static fromNanoseconds(ns: bigint) {
        return new Duration(getInstantPart(ns as NANOS_OF_EPOCH, InstantPart.all));
    }

    [Equatable.equals](other: unknown) {
        return other instanceof Duration
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = DURATION_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.years);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.months);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.days);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.hours);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.minutes);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.seconds);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.milliseconds);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.nanoseconds);
        return hc;
    }
}

Object.defineProperty(Duration.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "Duration"
});


function formatUnit(value: number, plural: string, singular: string) {
    return `${value} ${Math.abs(value) === 1 ? singular : plural}`;
}